import React, {
  ComponentProps,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import PropTypes from 'prop-types';

import Animation from '@adsk/alloy-react-animation';
import Divider from '@adsk/alloy-react-divider';
import { sva, useKeyEvent } from '@adsk/alloy-react-helpers';
import { useTheme } from '@adsk/alloy-react-styles';
import { StylableRenderPropType, stylePropType } from '@adsk/alloy-react-theme';

import { WIDTHS } from './consts';
import { Body, Container, Header, HeaderProps } from './layout';

const ESCAPE = 'Escape';

const defaultRenderHeader = (props: HeaderProps) => <Header {...props} />;

export type PanelProps = Omit<ComponentProps<typeof Animation>, 'title'> & {
  renderHeader?: (props: HeaderProps) => ReactElement;
  renderSubHeader?: (props: StylableRenderPropType) => ReactElement;
  renderFooter?: (
    props: StylableRenderPropType & {
      onClose?: () => void;
    },
  ) => ReactElement;
  open?: boolean;
  width?: number | string;
  floating?: boolean;
  title?: ReactNode;
  onClose?: () => void;
  disableAnimation?: boolean;
  shouldCloseOnEsc?: boolean;
  minWidth?: number;
};

/**
 * A Panel component.  Provides access to secondary content which only appears when specifically requested by a user.
 */
const Panel = React.forwardRef<HTMLDivElement, PanelProps>(
  (
    {
      children,
      className,
      title,
      onClose,
      onAnimationStart,
      onAnimationComplete,
      renderHeader = defaultRenderHeader,
      renderSubHeader,
      renderFooter,
      open = false,
      style,
      width = WIDTHS.MEDIUM,
      floating = false,
      disableAnimation = false,
      shouldCloseOnEsc = true,
      minWidth,
      ...props
    },
    ref,
  ) => {
    const dynamicTheme = useTheme();
    const panelTokens = dynamicTheme.tokens?.component?.panel;

    const innerRef = useRef<HTMLDivElement>(null);
    const [isFullyOpen, setIsFullyOpen] = useState<boolean | undefined>(open);
    const onEscKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === ESCAPE) {
          shouldCloseOnEsc && onClose?.();
        }
      },
      [shouldCloseOnEsc, onClose],
    );

    useKeyEvent(ESCAPE, { onKeyDown: onEscKeyDown });

    const handleOnAnimationComplete: typeof onAnimationComplete = (x) => {
      setIsFullyOpen(open);
      onAnimationComplete && onAnimationComplete(x);
    };

    const motionProps = useMemo(
      () => ({ width: !open ? minWidth || 0 : width }),
      [open, width, minWidth],
    );

    const containerStyle = useMemo(
      () =>
        sva(panelTokens?.container?._, {
          variants: {
            open: panelTokens?.container?.open,
            floating: panelTokens?.container?.floating,
          },
          compoundVariants: panelTokens?.container?.compoundVariants,
        }),
      [panelTokens],
    );

    const renderContents = () => (
      <div
        ref={innerRef}
        css={[
          {
            display: 'flex',
            width: '100%',
            flexDirection: 'column',
            height: '100%',
          },
          panelTokens?.contents?.open?.[isFullyOpen && open ? 'true' : 'false'],
        ]}
      >
        {renderHeader({ title, onClose, showEscapeLabel: shouldCloseOnEsc })}
        {renderSubHeader?.({
          style: panelTokens?.subHeader ?? {},
        })}
        {!!renderSubHeader && <Divider style={panelTokens?.divider} />}
        {children}
        {!!renderFooter && <Divider style={panelTokens?.divider} />}
        {renderFooter?.({
          style: panelTokens?.footer ?? {},
          onClose,
        })}
      </div>
    );

    return disableAnimation ? (
      <div
        {...props}
        ref={ref}
        className={className}
        css={[
          containerStyle({ open, floating }),
          { width: open ? width : 0 },
          style,
        ]}
      >
        {renderContents()}
      </div>
    ) : (
      <Animation
        {...props}
        ref={ref}
        className={className}
        style={[containerStyle({ open, floating }), style]}
        initial={motionProps}
        animate={motionProps}
        transition={{
          ease: 'easeOut',
          duration: 0.3,
        }}
        onAnimationStart={onAnimationStart}
        onAnimationComplete={handleOnAnimationComplete}
      >
        {renderContents()}
      </Animation>
    );
  },
);

Panel.displayName = 'Panel';

Panel.propTypes = {
  /** If panel is open or not */
  open: PropTypes.bool,
  /** Panel content */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Render custom header content. Props: ({ title, onClose}) */
  renderHeader: PropTypes.func,
  /** Render custom sub header content */
  renderSubHeader: PropTypes.func,
  /** Render custom footer content */
  renderFooter: PropTypes.func,
  /** Width of Panel.  Use Panel.WIDTHS for standard widths */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** If panel should overlay content.  Default false for small/medium, true for large */
  floating: PropTypes.bool,
  /** Title to display in header */
  title: PropTypes.any,
  /** Function executed upon clicking close button */
  onClose: PropTypes.func,
  /** Function executed upon end of animation */
  onAnimationComplete: PropTypes.func,
  /** Disable panel animations */
  disableAnimation: PropTypes.bool,
  /** Set whether the panel should be closed when pressing the ESC key */
  shouldCloseOnEsc: PropTypes.bool,
  /** Minimum width of panel when closed */
  minWidth: PropTypes.number,
};

export default Object.assign(Panel, {
  WIDTHS,
  Container,
  Header,
  Body,
});
